import {useNavigate} from 'react-router-dom'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useI18n} from '@/i18n'
import {useToast} from '@/hooks/use-toast'
import {routes} from '@/lib/routes'
import {useServices} from '@/services/context'
import type {LogLevel} from '@/types/background-jobs'

export interface UseJobMutationsOptions {
    /** Called by the schedule-update mutation when the user has explicitly requested
     * a save (e.g. to close the inline editor on success). */
    onScheduleSaved?: () => void
    /** Called by the schedule-update mutation when the server rejects the request,
     * so the page can render an inline ErrorBanner. */
    onScheduleError?: (error: Error) => void
}

/**
 * All mutations that JobDetail performs against `services.jobs.*`, packaged so the
 * page body never has to derive query-invalidation strings or success-toast keys
 * inline. Each returned member is a stock TanStack `useMutation` result — call
 * `.mutate(...)` / read `.isPending` / etc. exactly as before.
 */
export function useJobMutations(jobId: string | undefined, options: UseJobMutationsOptions = {}) {
    const services = useServices()
    const navigate = useNavigate()
    const i18n = useI18n()
    const {toast} = useToast()
    const {onScheduleSaved, onScheduleError} = options

    const toggle = useEntityMutation({
        mutationFn: (enabled: boolean) => services.jobs.update(jobId!, {enabled}),
        invalidate: ['job', jobId],
        onSuccess: (_, enabled) => {
            toast({description: enabled ? i18n.jobs.jobEnabled : i18n.jobs.jobDisabled})
        },
    })

    const trigger = useEntityMutation({
        mutationFn: () => services.jobs.trigger(jobId!),
        invalidate: [['job', jobId], ['job-executions', jobId]],
        successMessage: i18n.jobs.jobTriggered,
    })

    const updateTaskLogLevel = useEntityMutation({
        mutationFn: ({taskId, logLevel}: {taskId: string; logLevel: LogLevel}) =>
            services.jobs.updateTaskLogLevel(taskId, {logLevel}),
        invalidate: ['job-tasks', jobId],
    })

    const deleteJob = useEntityMutation({
        mutationFn: () => services.jobs.remove(jobId!),
        invalidate: ['jobs'],
        successMessage: i18n.jobs.jobDeleted,
        onSuccess: () => {
            navigate(routes.backgroundJobs.jobs.list())
        },
    })

    const updateSchedule = useEntityMutation({
        mutationFn: (cronExpression: string | null) =>
            services.jobs.schedule(jobId!, {cronExpression}),
        invalidate: ['job', jobId],
        successMessage: i18n.scriptEditor.schedule.saved,
        onSuccess: () => {
            onScheduleSaved?.()
        },
        onError: (error) => {
            onScheduleError?.(error as Error)
        },
    })

    return {toggle, trigger, updateTaskLogLevel, deleteJob, updateSchedule}
}
